import { Box, MenuItem, Stack, TextField, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { ART_TIER_COUNT } from '../../shared/layer-stack'
import type { PerSlugRow } from '../../shared/section-form'

export interface PerOptionTierEditorProps {
  rows: PerSlugRow[]
  isColour: boolean
  tierCount?: number
  onChange: (key: string, tier: number) => void
  onSetAll: (tier: number) => void
}

export default function PerOptionTierEditor({
  rows,
  isColour,
  tierCount = ART_TIER_COUNT,
  onChange,
  onSetAll,
}: PerOptionTierEditorProps) {
  const tiers = Array.from({ length: tierCount }, (_v, i) => i)

  const { formatMessage } = useIntl()

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {formatMessage({
          id: isColour
            ? 'avatarStudio.editor.perOption.noColours'
            : 'avatarStudio.editor.perOption.noOptions',
        })}
      </Typography>
    )
  }

  return (
    <Stack spacing={1} data-testid="per-option-tiers">
      <Typography variant="caption" color="text.secondary">
        {formatMessage({ id: 'avatarStudio.editor.perOption.hint' })}
      </Typography>

      <TextField
        select={true}
        size="small"
        sx={{ width: 200 }}
        label={formatMessage({ id: 'avatarStudio.editor.perOption.setAll' })}
        value=""
        onChange={(e) => onSetAll(Number(e.target.value))}
        data-testid="per-option-set-all"
      >
        {tiers.map((tier) => (
          <MenuItem key={tier} value={String(tier)}>
            {formatMessage({ id: 'avatarStudio.option.tierBadge' }, { tier })}
          </MenuItem>
        ))}
      </TextField>

      {rows.map((row) => (
        // The row is a named group, so entering it announces WHICH option is being set. MUI's
        // Select appends its own id to any aria-labelledby, so naming the select itself is not
        // reliable; the group is.
        <Stack
          key={row.key}
          role="group"
          aria-labelledby={`per-option-label-${row.key}`}
          direction="row"
          spacing={1.5}
          alignItems="center"
          data-testid={`per-option-${row.key}`}
        >
          {isColour && (
            <Box
              role="img"
              aria-label={formatMessage(
                { id: 'avatarStudio.editor.swatches.aria' },
                { hex: row.key },
              )}
              sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: `#${row.key}`,
              }}
            />
          )}
          <Typography
            id={`per-option-label-${row.key}`}
            variant="body2"
            noWrap={true}
            translate={isColour ? 'no' : undefined}
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            {row.label}
          </Typography>
          <TextField
            select={true}
            size="small"
            sx={{ width: 128, flexShrink: 0 }}
            label={formatMessage({ id: 'avatarStudio.editor.perOption.tier' })}
            value={String(row.tier)}
            onChange={(e) => onChange(row.key, Number(e.target.value))}
          >
            {tiers.map((tier) => (
              <MenuItem key={tier} value={String(tier)}>
                {formatMessage({ id: 'avatarStudio.option.tierBadge' }, { tier })}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ))}
    </Stack>
  )
}
